import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Loader2, Calculator, IndianRupee, Calendar, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { key: 'annual', label: 'Annual (1 payment)',   instalments: 1, months: ['Apr-Mar'] },
  { key: 'half',   label: 'Half-yearly (2)',      instalments: 2, months: ['Apr-Sep', 'Oct-Mar'] },
  { key: 'term',   label: '3 Terms (most common)',instalments: 3, months: ['Apr-Jul', 'Aug-Nov', 'Dec-Mar'] },
  { key: 'quart',  label: 'Quarterly (4)',        instalments: 4, months: ['Apr-Jun', 'Jul-Sep', 'Oct-Dec', 'Jan-Mar'] },
  { key: 'month',  label: 'Monthly (10, Apr-Jan)',instalments: 10, months: null },
];

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const academicYearAuto = () => {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const monthIndexInAY = (i, instalments) => {
  // For monthly: month 0=Apr, 1=May, ... 9=Jan
  // For others: derive sensible default due dates spread across the AY
  return Math.floor((i * 11) / instalments) + 3; // 3=April
};

const defaultDueDateForInstalment = (i, instalments, ay) => {
  const startYear = parseInt(ay.split('-')[0], 10);
  const monthIdx = monthIndexInAY(i, instalments); // 3..13
  const year = startYear + (monthIdx > 11 ? 1 : 0);
  const month = ((monthIdx) % 12); // 0-11
  return new Date(Date.UTC(year, month, 15)).toISOString().slice(0, 10);
};

/**
 * Per-student fee structure editor (modal-style).
 * - HT picks an annual total + instalment split (preset OR custom)
 * - Each instalment row is editable (label, amount, due date)
 * - "Auto-split equally" button divides annual total evenly
 * - Saves to /api/head-teacher/students/{id}/fee-structure
 */
const StudentFeeStructureModal = ({ student, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ay, setAy] = useState(academicYearAuto());
  const [annual, setAnnual] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [notes, setNotes] = useState('');
  const [instalments, setInstalments] = useState([]);
  const [summary, setSummary] = useState(null);

  // Load existing structure if any
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API}/head-teacher/students/${student.id}/fee-summary?academic_year=${ay}`
      );
      setSummary(data);
      if (data.structure) {
        setAnnual(String(data.structure.annual_total));
        setDiscount(String(data.structure.discount || ''));
        setDiscountReason(data.structure.discount_reason || '');
        setNotes(data.structure.notes || '');
        setInstalments(data.structure.instalments.map(i => ({ ...i })));
      } else {
        setInstalments([]);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [student.id, ay]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (preset) => {
    const n = preset.instalments;
    const total = parseFloat(annual) || 0;
    const per = total > 0 ? Math.round(total / n) : 0;
    const list = Array.from({ length: n }).map((_, i) => {
      const isLast = i === n - 1;
      // last one absorbs rounding remainder
      const amt = isLast && total > 0 ? total - per * (n - 1) : per;
      return {
        label: n === 1 ? 'Annual Fee'
              : n === 10 ? `Month ${i + 1} (${['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan'][i]})`
              : preset.months && preset.months[i] ? `${['Term','Half','Quarter'][n === 2 ? 1 : (n === 4 ? 2 : 0)]} ${i + 1} (${preset.months[i]})`
              : `Instalment ${i + 1}`,
        amount: amt,
        due_date: defaultDueDateForInstalment(i, n, ay),
      };
    });
    setInstalments(list);
  };

  const autoSplit = () => {
    if (!instalments.length || !annual) return;
    const n = instalments.length;
    const total = parseFloat(annual);
    const per = Math.round(total / n);
    setInstalments(prev => prev.map((row, i) => ({
      ...row,
      amount: i === n - 1 ? total - per * (n - 1) : per,
    })));
  };

  const addRow = () => {
    setInstalments(prev => [
      ...prev,
      { label: `Instalment ${prev.length + 1}`, amount: 0, due_date: defaultDueDateForInstalment(prev.length, prev.length + 1, ay) },
    ]);
  };

  const updateRow = (i, field, value) => {
    setInstalments(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: field === 'amount' ? parseFloat(value || 0) : value } : r));
  };

  const removeRow = (i) => setInstalments(prev => prev.filter((_, idx) => idx !== i));

  const total = instalments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const target = parseFloat(annual) || 0;
  const diff = target - total;
  const balanced = Math.abs(diff) <= 1 && target > 0 && instalments.length > 0;

  const save = async () => {
    if (!annual || target <= 0) { toast.error('Enter annual fee'); return; }
    if (!instalments.length) { toast.error('Add at least one instalment'); return; }
    if (!balanced) { toast.error(`Instalments must total ₹${fmt(target)} (current: ₹${fmt(total)})`); return; }
    if (instalments.some(i => !i.label || !i.due_date || (parseFloat(i.amount) || 0) <= 0)) {
      toast.error('Each instalment needs a label, amount > 0 and due date');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/head-teacher/students/${student.id}/fee-structure`, {
        academic_year: ay,
        annual_total: target,
        discount: parseFloat(discount) || 0,
        discount_reason: discountReason || null,
        notes,
        instalments: instalments.map(i => ({
          label: i.label, amount: parseFloat(i.amount), due_date: i.due_date,
        })),
      });
      toast.success('Fee structure saved');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" data-testid="fee-structure-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h3 className="text-lg font-bold">Fee Structure · {student.name}</h3>
            <p className="text-xs text-orange-50">Class {student.class_level} · Roll {student.roll_number || '—'} · Academic Year {ay}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="fee-modal-close"><X size={22} /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="mx-auto animate-spin text-orange-500" /></div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Summary chips when existing structure */}
            {summary && summary.structure && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-emerald-100 text-emerald-700 border-0">Paid ₹{fmt(summary.paid)}</Badge>
                <Badge className="bg-amber-100 text-amber-700 border-0">Pending ₹{fmt(summary.pending)}</Badge>
                {summary.overdue > 0 && <Badge className="bg-red-100 text-red-700 border-0">Overdue ₹{fmt(summary.overdue)}</Badge>}
              </div>
            )}

            {/* Step 1: Annual total */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                  <IndianRupee size={12} /> Net annual fee (after discount)
                </label>
                <input
                  type="number"
                  value={annual}
                  onChange={e => setAnnual(e.target.value)}
                  placeholder="e.g. 24000"
                  className="w-full mt-1.5 border rounded-lg px-3 py-2 text-lg font-bold text-gray-900 focus:border-orange-400 focus:outline-none"
                  data-testid="fee-annual-total"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                  <Calendar size={12} /> Academic year
                </label>
                <input
                  type="text"
                  value={ay}
                  onChange={e => setAy(e.target.value)}
                  placeholder="2026-27"
                  className="w-full mt-1.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  data-testid="fee-ay"
                />
              </div>
            </div>

            {/* Discount row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-emerald-50/40 border border-emerald-200 rounded-lg p-3">
              <div>
                <label className="text-xs font-semibold text-emerald-800 uppercase tracking-wide flex items-center gap-1">
                  <IndianRupee size={12} /> Discount (optional)
                </label>
                <input
                  type="number"
                  value={discount}
                  onChange={e => setDiscount(e.target.value)}
                  placeholder="e.g. 1500"
                  className="w-full mt-1.5 border rounded-lg px-3 py-2 text-base font-semibold text-emerald-700 focus:border-emerald-400 focus:outline-none"
                  data-testid="fee-discount"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Reason</label>
                <input
                  type="text"
                  value={discountReason}
                  onChange={e => setDiscountReason(e.target.value)}
                  placeholder="Sibling / Scholarship / Staff child …"
                  className="w-full mt-1.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  data-testid="fee-discount-reason"
                />
              </div>
              {parseFloat(discount) > 0 && parseFloat(annual) > 0 && (
                <p className="sm:col-span-2 text-xs text-emerald-700">
                  Gross fee: ₹{fmt(parseFloat(annual) + parseFloat(discount))} − Discount ₹{fmt(parseFloat(discount))} = <strong>Net ₹{fmt(parseFloat(annual))}</strong>
                </p>
              )}
            </div>

            {/* Step 2: Pick a preset */}
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1 mb-2">
                <Sparkles size={12} /> Choose a payment plan
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PRESETS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p)}
                    disabled={!annual}
                    className="text-left border-2 rounded-lg p-2 text-xs hover:border-orange-400 hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid={`fee-preset-${p.key}`}
                  >
                    <p className="font-semibold text-gray-800">{p.label.split('(')[0].trim()}</p>
                    <p className="text-[11px] text-gray-500">{p.label.match(/\((.*?)\)/)?.[1]}</p>
                    {parseFloat(annual) > 0 && (
                      <p className="text-orange-600 font-bold mt-1">₹{fmt(parseFloat(annual) / p.instalments)} × {p.instalments}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Edit instalments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Instalments</label>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={autoSplit} disabled={!instalments.length || !annual} className="h-7 text-xs" data-testid="fee-auto-split">
                    <Calculator size={12} className="mr-1" /> Auto-split equally
                  </Button>
                  <Button size="sm" variant="outline" onClick={addRow} className="h-7 text-xs" data-testid="fee-add-instalment">
                    <Plus size={12} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              {instalments.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-400 text-sm">
                  Pick a payment plan above, or click <span className="font-medium">Add</span> to create instalments manually.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {instalments.map((row, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-orange-50/30 border border-orange-100 rounded-lg p-2">
                      <input
                        type="text"
                        value={row.label}
                        onChange={e => updateRow(i, 'label', e.target.value)}
                        placeholder="Term 1 (Apr-Jul)"
                        className="col-span-5 border rounded px-2 py-1.5 text-sm focus:outline-none"
                        data-testid={`fee-inst-label-${i}`}
                      />
                      <div className="col-span-3 relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                        <input
                          type="number"
                          value={row.amount}
                          onChange={e => updateRow(i, 'amount', e.target.value)}
                          className="w-full border rounded pl-5 pr-2 py-1.5 text-sm text-right focus:outline-none tabular-nums"
                          data-testid={`fee-inst-amount-${i}`}
                        />
                      </div>
                      <input
                        type="date"
                        value={row.due_date}
                        onChange={e => updateRow(i, 'due_date', e.target.value)}
                        className="col-span-3 border rounded px-2 py-1.5 text-sm focus:outline-none"
                        data-testid={`fee-inst-due-${i}`}
                      />
                      <button onClick={() => removeRow(i)} className="col-span-1 text-red-500 hover:text-red-700 text-center" data-testid={`fee-inst-remove-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals row */}
            <div className={`rounded-lg p-3 border-2 ${balanced ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700">Instalments total</span>
                <span className="font-bold tabular-nums">₹{fmt(total)} / ₹{fmt(target)}</span>
              </div>
              {!balanced && target > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  {diff > 0 ? `₹${fmt(diff)} short — add or increase an instalment.` : `₹${fmt(-diff)} extra — reduce an instalment.`}
                </p>
              )}
              {balanced && <p className="text-xs text-emerald-700 mt-1">✓ Balanced — ready to save.</p>}
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Sibling discount applied, scholarship 50%, etc."
                rows={2}
                className="w-full mt-1.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                data-testid="fee-notes"
              />
            </div>

            {/* Save */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button
                onClick={save}
                disabled={saving || !balanced}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="fee-save-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Save fee structure
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentFeeStructureModal;
